import Cookies from 'js-cookie';
import { apiRequest } from '@/utils/api-request';
import type {
  Student, StudentListResponse, StudentFormData,
  StudentContact, MedicalInfo, StudentStatus, StudentDocument
} from './types';
import type {
  StudentReportCard, StudentAttendance, StudentAttendanceSummary,
  StudentFeeLedger, StudentConcerns
} from './profile/profile.types';

const _apiBase = (): string =>
  (import.meta.env.VITE_API_URL as string) || 'http://localhost:8000/api';

const BASE = '/api/parent-app/admin/students';

export const studentsApi = {
  list: (params: Record<string, string | number>) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest<StudentListResponse>(`${BASE}?${qs}`, { method: 'GET' });
  },

  create: (data: Partial<StudentFormData>) =>
    apiRequest<Student>(BASE, { method: 'POST', body: JSON.stringify(data) }),

  get: (uuid: string) =>
    apiRequest<Student>(`${BASE}/${uuid}`, { method: 'GET' }),

  update: (uuid: string, data: Partial<StudentFormData>) =>
    apiRequest<Student>(`${BASE}/${uuid}`, { method: 'PUT', body: JSON.stringify(data) }),

  updateStatus: (uuid: string, status: StudentStatus) =>
    apiRequest<Student>(`${BASE}/${uuid}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),

  // Bulk ops use integer IDs in the request body (not URL)
  bulkStatus: (student_ids: number[], status: StudentStatus) =>
    apiRequest(`${BASE}/bulk/status`, { method: 'POST', body: JSON.stringify({ student_ids, status }) }),

  bulkClassAssign: (student_ids: number[], class_id: number, section_id?: number) =>
    apiRequest(`${BASE}/bulk/class-assign`, { method: 'POST', body: JSON.stringify({ student_ids, class_id, section_id }) }),

  bulkInvite: (student_ids: number[]) =>
    apiRequest(`${BASE}/bulk/invite`, { method: 'POST', body: JSON.stringify({ student_ids }) }),

  getContacts: (uuid: string) =>
    apiRequest<StudentContact[]>(`${BASE}/${uuid}/contacts`, { method: 'GET' }),

  createContact: (uuid: string, data: Partial<StudentContact>) =>
    apiRequest<StudentContact>(`${BASE}/${uuid}/contacts`, { method: 'POST', body: JSON.stringify(data) }),

  updateContact: (uuid: string, contactId: number, data: Partial<StudentContact>) =>
    apiRequest<StudentContact>(`${BASE}/${uuid}/contacts/${contactId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteContact: (uuid: string, contactId: number) =>
    apiRequest(`${BASE}/${uuid}/contacts/${contactId}`, { method: 'DELETE' }),

  sendInvite: (uuid: string, contactId: number) =>
    apiRequest(`${BASE}/${uuid}/contacts/${contactId}/invite`, { method: 'POST' }),

  addSibling: (uuid: string, related_student_id: number, relation_type: string) =>
    apiRequest(`${BASE}/${uuid}/siblings`, { method: 'POST', body: JSON.stringify({ related_student_id, relation_type }) }),

  removeSibling: (uuid: string, relId: number) =>
    apiRequest(`${BASE}/${uuid}/siblings/${relId}`, { method: 'DELETE' }),

  addExternalSibling: (uuid: string, data: { sibling_name: string; age?: number; school_name?: string; notes?: string }) =>
    apiRequest(`${BASE}/${uuid}/external-siblings`, { method: 'POST', body: JSON.stringify(data) }),

  removeExternalSibling: (uuid: string, sibId: number) =>
    apiRequest(`${BASE}/${uuid}/external-siblings/${sibId}`, { method: 'DELETE' }),

  getMedical: (uuid: string) =>
    apiRequest<MedicalInfo>(`${BASE}/${uuid}/medical`, { method: 'GET' }),

  upsertMedical: (uuid: string, data: Partial<MedicalInfo>) =>
    apiRequest<MedicalInfo>(`${BASE}/${uuid}/medical`, { method: 'PUT', body: JSON.stringify(data) }),

  uploadPhoto: async (uuid: string, file: File) => {
    const token = Cookies.get('erp_auth_token') || Cookies.get('crm_auth_token');
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const res = await fetch(`${_apiBase()}/parent-app/admin/students/${uuid}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}` },
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      return { success: data.success || res.ok, data: data.data as { photo_url: string } | undefined, message: data.message as string | undefined };
    } catch (err: any) {
      return { success: false, message: err.message as string };
    }
  },

  listDocuments: (uuid: string) =>
    apiRequest<StudentDocument[]>(`${BASE}/${uuid}/documents`, { method: 'GET' }),

  uploadDocument: async (uuid: string, file: File, title: string, doc_type: string) => {
    const token = Cookies.get('erp_auth_token') || Cookies.get('crm_auth_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('doc_type', doc_type);
    try {
      const res = await fetch(`${_apiBase()}/parent-app/admin/students/${uuid}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}` },
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      return { success: data.success || res.ok, data: data.data as StudentDocument | undefined, message: data.message as string | undefined };
    } catch (err: any) {
      return { success: false, message: err.message as string };
    }
  },

  deleteDocument: (uuid: string, docId: number) =>
    apiRequest(`${BASE}/${uuid}/documents/${docId}`, { method: 'DELETE' }),

  // ── 360° profile ──
  // report card: omit term_id → all terms (Academics); pass published=true for
  // published-only (Report Cards tab).
  reportCard: (uuid: string, opts?: { term_id?: number; published?: boolean }) => {
    const qs = new URLSearchParams();
    if (opts?.term_id) qs.set('term_id', String(opts.term_id));
    if (opts?.published) qs.set('published', 'true');
    const q = qs.toString();
    return apiRequest<StudentReportCard>(`${BASE}/${uuid}/report-card${q ? `?${q}` : ''}`, { method: 'GET' });
  },

  attendance: (uuid: string, month?: string) => {
    const q = month ? `?month=${month}` : '';
    return apiRequest<StudentAttendance>(`${BASE}/${uuid}/attendance${q}`, { method: 'GET' });
  },

  attendanceSummary: (uuid: string, months = 3) =>
    apiRequest<StudentAttendanceSummary>(`${BASE}/${uuid}/attendance-summary?months=${months}`, { method: 'GET' }),

  fees: (uuid: string) =>
    apiRequest<StudentFeeLedger>(`${BASE}/${uuid}/fees`, { method: 'GET' }),

  concerns: (uuid: string) =>
    apiRequest<StudentConcerns>(`${BASE}/${uuid}/concerns`, { method: 'GET' }),
};
